import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { EnvironmentService } from '@nomoh-monorepo/shared-data-access-environment';
import {
  IPessoa,
  IPlano,
  IRedefinirSenhaModel,
  IResponse,
} from '@nomoh-monorepo/shared-types';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class PessoaDataService {
  httpClient = inject(HttpClient);
  environment = inject(EnvironmentService);

  private baseUrl = `${this.environment.getApiUrl('nomoh')}/pessoa`;
  private baseUrlCliente = `${this.environment.getApiUrl(
    'nomoh'
  )}/pessoa-cliente`;

  private readonly _userInfo = signal<IPessoa | null>({} as IPessoa);

  $userInfo = this._userInfo.asReadonly();

  clearUserInfo() {
    this._userInfo.set(null);
  }

  private setUserInfo(pessoa: IPessoa | null) {
    this._userInfo.set(pessoa);
  }

  async buscarPessoaLogada(): Promise<IPessoa | null> {
    try {
      const pessoa = await firstValueFrom(
        this.httpClient.get<IPessoa>(`${this.baseUrl}/findById`)
      );
      this.setUserInfo(pessoa);
      return pessoa;
    } catch (error) {
      console.error('Erro ao buscar as informações do usuário!', error);
      return null;
    }
  }

  /**
   * Busca uma pessoa completa pelo ID (incluindo endereço)
   * Endpoint: GET /pessoa-cliente/findById?id=${id}
   * Baseado em: temp_portal GestaoPessoaService.buscarPessoaPorId()
   */
  async buscarPorId(id: string): Promise<IPessoa | null> {
    try {
      return await firstValueFrom(
        this.httpClient.get<IPessoa>(`${this.baseUrlCliente}/findById?id=${id}`)
      );
    } catch (error) {
      return null;
    }
  }

  /**
   * Busca planos associados a uma pessoa
   * Endpoint: GET /pessoa-cliente/findPlanos?id=${pessoaId}
   * Baseado em: temp_portal GestaoPessoaService.buscarPlanosAssociados()
   */
  async buscarPlanos(pessoaId: string): Promise<IPlano[]> {
    try {
      return await firstValueFrom(
        this.httpClient.get<IPlano[]>(
          `${this.baseUrlCliente}/findPlanos?id=${pessoaId}`
        )
      );
    } catch {
      return [];
    }
  }

  /**
   * Desvincula um plano de uma pessoa
   * Endpoint: POST /pessoa-cliente/desvincularPlano
   * Body: { pessoaId: string, planosId: string }
   * Baseado em: temp_portal GestaoPessoaService.desvincularPlano()
   */
  async desvincularPlano(pessoaId: string, planoId: string): Promise<boolean> {
    try {
      return await firstValueFrom(
        this.httpClient.post<boolean>(
          `${this.baseUrlCliente}/desvincularPlano`,
          {
            pessoaId,
            planosId: planoId,
          }
        )
      );
    } catch {
      return false;
    }
  }

  /**
   * Busca todos os planos ativos disponíveis para compra (sem permissão admin)
   * Endpoint: GET /plano/findAtivos
   */
  async buscarTodosPlanos(): Promise<IPlano[]> {
    try {
      const planoUrl = `${this.environment.getApiUrl(
        'nomoh'
      )}/plano/findAtivos`;
      const response = await firstValueFrom(
        this.httpClient.get<IResponse<IPlano[]>>(planoUrl)
      );
      return response.data || [];
    } catch {
      return [];
    }
  }

  /**
   * Vincula um plano a uma pessoa
   * Lógica: Se já tem plano do mesmo sistema, faz UPDATE (troca). Se não tem, faz INSERT (adiciona novo).
   * Endpoint: POST /pessoa-cliente/vincularPlano
   * Body: { pessoaId: string, planoId: string, isUpdate: boolean }
   */
  async vincularPlano(
    pessoaId: string,
    planoId: string,
    isUpdate: boolean
  ): Promise<boolean> {
    try {
      return await firstValueFrom(
        this.httpClient.post<boolean>(`${this.baseUrlCliente}/vincularPlano`, {
          pessoaId,
          planoId,
          isUpdate,
        })
      );
    } catch {
      return false;
    }
  }

  /**
   * Ativa o plano gratuito do Bardo para o usuário logado (self-service, trial 7 dias)
   * Endpoint: POST /pessoa-cliente/ativarPlanoGratuitoBardo
   */
  async ativarPlanoGratuitoBardo(): Promise<void> {
    await firstValueFrom(
      this.httpClient.post(
        `${this.baseUrlCliente}/ativarPlanoGratuitoBardo`,
        {}
      )
    );
  }

  async recuperarToken(dadosContato: string) {
    return await firstValueFrom(
      this.httpClient.get(
        `${this.baseUrl}/recuperarSenha?dadosContato=${dadosContato}`
      )
    );
  }

  async alterarSenha(redefinirSenha: IRedefinirSenhaModel) {
    return await firstValueFrom(
      this.httpClient.post<boolean>(
        `${this.baseUrl}/redefinirSenha`,
        redefinirSenha
      )
    );
  }

  /**
   * Cria um novo usuário no sistema.
   * @param usuario Dados do usuário a ser criado.
   * @param sistemaId ID do sistema para associar o plano padrão automaticamente (opcional).
   *                  Se fornecido, o backend associa o plano padrão do sistema ao usuário.
   * @returns Promise com o ID do usuário criado.
   */
  async adicionarUsuario(usuario: IPessoa, sistemaId?: string) {
    const url = sistemaId
      ? `${this.baseUrl}/create?sistemaId=${sistemaId}`
      : `${this.baseUrl}/create`;

    return firstValueFrom(this.httpClient.post(url, usuario));
  }

  async atualizarUsuario(usuario: IPessoa) {
    return firstValueFrom(
      this.httpClient.put<any>(`${this.baseUrl}/update`, usuario)
    );
  }

  /**
   * Gera URL do Customer Portal do Stripe para o cliente gerenciar assinatura.
   * Endpoint: POST /pagamento/portal
   */
  async abrirPortalAssinatura(): Promise<{ url: string } | null> {
    try {
      const pagamentoUrl = `${this.environment.getApiUrl(
        'nomoh'
      )}/pagamento/portal`;
      return await firstValueFrom(
        this.httpClient.post<{ url: string }>(pagamentoUrl, {})
      );
    } catch {
      return null;
    }
  }
}
